import type { Deal } from "../types";

/**
 * Currency helpers for deals (see ADR-010).
 *
 * A deal stores `amount` in its own `currency` plus a snapshotted
 * `exchange_rate` (units of the reporting currency per one unit of
 * `currency`). The database derives `amount_cad` from those two values.
 * Every aggregate must read `amount_cad`, never `amount`.
 */

/** Browser locale for number formatting; never hardcode "en-US" (ADR-006). */
export const getUserLocale = (): string =>
  (typeof navigator !== "undefined" && navigator.languages?.[0]) || "en-US";

/** Mirrors the DB generated column: round(amount * exchange_rate). */
export const toReportingAmount = (
  amount: number | null | undefined,
  exchangeRate: number | null | undefined,
): number => Math.round((amount ?? 0) * (exchangeRate ?? 1));

/**
 * Format a monetary value. Amounts in the reporting currency use a bare
 * symbol; any other currency is prefixed with its ISO code so a USD figure
 * is never shown as an ambiguous "$".
 */
export const formatAmount = (
  value: number,
  currency: string,
  reportingCurrency: string,
  locale: string,
  options: Intl.NumberFormatOptions = {},
): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: currency === reportingCurrency ? "narrowSymbol" : "code",
    ...options,
  }).format(value);

export type DealAmountLabel = {
  /** The amount in the deal's own currency. */
  native: string;
  /** The reporting-currency equivalent, only when the deal is in another currency. */
  reporting?: string;
};

/**
 * Per-deal display: the native amount, plus the reporting-currency
 * equivalent when the two differ. Legacy records without a currency are
 * treated as reporting-currency deals.
 */
export const formatDealAmount = (
  deal: Pick<Deal, "amount" | "currency" | "exchange_rate" | "amount_cad">,
  reportingCurrency: string,
  locale: string,
  options: Intl.NumberFormatOptions = {},
): DealAmountLabel => {
  const currency = deal.currency || reportingCurrency;
  const native = formatAmount(
    deal.amount ?? 0,
    currency,
    reportingCurrency,
    locale,
    options,
  );
  if (currency === reportingCurrency) return { native };
  const reportingValue =
    deal.amount_cad ?? toReportingAmount(deal.amount, deal.exchange_rate);
  return {
    native,
    reporting: formatAmount(
      reportingValue,
      reportingCurrency,
      reportingCurrency,
      locale,
      options,
    ),
  };
};

/** Compact "$45K"-style options shared by cards and lists. */
export const compactAmountOptions: Intl.NumberFormatOptions = {
  notation: "compact",
  minimumSignificantDigits: 3,
};

/** Reporting-currency value of one deal, falling back to amount × rate when the DB column is absent. */
export const reportingAmountOf = (
  deal: Pick<Deal, "amount" | "exchange_rate" | "amount_cad">,
): number =>
  deal.amount_cad ?? toReportingAmount(deal.amount, deal.exchange_rate);

/** Sum of reporting-currency values; the only correct way to total deals. */
export const sumReportingAmount = (
  deals: Pick<Deal, "amount" | "exchange_rate" | "amount_cad">[],
): number => deals.reduce((sum, deal) => sum + reportingAmountOf(deal), 0);
